/**
 * Ask — C3 and C5, which are ONE renderer in two states.
 *
 * C3 is the app's request, read-only: nothing is negotiable until a realm is
 * chosen, because the realm decides which rows are reachable at all. C5 is the
 * same list turned actionable. Rendering them as two components would recreate
 * the old page's split between "what this app needs" and "restrict further" —
 * two mental models for one decision.
 *
 * Nothing here knows which of the 22 edges it is serving.
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TypeName } from './TypeName';
import { metaOf, keyOf } from '../../pages/authorization/grantAxis';
import { categorizeDlr } from '../../utils/humanizeDlr';
import useClickOutside from '../../shared/useClickOutside';
import { fmt } from '../../nucleus/format';
import { usePluginNames, useToolFacts, toolNameOf } from '../../pages/plugins/data/elsewhere';
import { accessWord, toolAsksLine } from '../../pages/plugins/data/words';
import './tokens.css';
import './Ask.css';

/** G5 — the toggle. Carries the severity tone and the lock state. */
/** G5 — a verb the app asked for, and whether the chosen realms grant it.
 *
 *  NOT A CONTROL. The DLR is what the app asked for: a fact about the request,
 *  not a menu. What decides whether any of it is granted is the realm — the
 *  ceiling IS the grant — so the only thing to operate on this page is the
 *  realm list. Rendering these as buttons invited people to untick a statement
 *  of fact, and the copy beside them told them to.
 */
/**
 * THE VERB KEY — a fixed frame, so rows can be compared rather than read.
 *
 * The verbs used to render as however many words the ask happened to name, so
 * `action create edit read` and `goal read` had nothing in common visually:
 * you compared two rows by reading both. A fixed set of slots means the SHAPE
 * carries the answer — the same letter sits in the same place on every row, and
 * a filled column down the page is a verb this app holds everywhere.
 *
 * THE LETTERS ARE NOT NEW. R/W/E/D/P is the mapping the old RestrictFurtherPanel
 * used (`VERB_TO_LETTER`), so this is the vocabulary the product already had
 * rather than a second one invented here. `W` is create — write.
 *
 * THREE STATES, because two would lose the distinction the page exists to make:
 *   held     — asked for, and the chosen realms reach it
 *   withheld — asked for, and they do not (the strike-through, in one letter)
 *   unasked  — never requested. Present as a placeholder so the frame holds its
 *              shape, and quiet enough that it cannot be mistaken for granted.
 *
 * There is no sixth letter. `nodes:modify` used to need one — it granted edit
 * and delete without being either — and it lit both rather than claiming a slot
 * of its own. The verb is retired (migration 245), so the five here are now the
 * whole node vocabulary rather than a view of it.
 */
// WORDS, NOT INITIALS. R/W/E/D/P is compact and unreadable without a legend —
// and a consent page cannot ask someone to learn a key before they can read
// what they are granting. The fixed FRAME does the comparing work regardless:
// the same word sits in the same column on every row, so a filled column still
// scans down the page. Mono keeps the columns true.
const KEY_ORDER = ['read', 'edit', 'create', 'delete', 'propose'];

/** Per-verb state for one card: verb -> {on, out}. One computation, so the
 *  tags and the sentence beneath them can never disagree. */
export function verbStates(grants = [], isOn = () => true, available) {
  const asked = new Map();
  for (const g of grants) {
    if (g.resource !== 'nodes') continue;
    const verbs = [g.verb];
    for (const v of verbs) {
      if (!KEY_ORDER.includes(v)) continue;
      // TWO FLAGS, NOT ONE. `on` is the person's side — is this granted; `out`
      // is the realm's — can it be reached at all. Collapsing them into a
      // single boolean loses the distinction the page exists to make: one is a
      // choice, the other is a fact about your realm.
      //
      // A verb reached by ANY of its grants is reached: two grants can name
      // the same slot from different schemas.
      const prev = asked.get(v) || { on: false, out: false };
      asked.set(v, {
        on: prev.on || isOn(g),
        out: prev.out || (available ? !available(g) : false),
      });
    }
  }
  return asked;
}

/* ── THE ASK IN PLAIN WORDS ────────────────────────────────────────────────────
 * The same grants the table shows, said as a handful of sentences a person reads
 * instead of scanning past. Derived, never authored: every line is built from
 * `ask.grants` and the DLR items, so the prose cannot claim what the table does
 * not grant.
 *
 *   types that share the same verbs    → one line   ("Read, create … your goals and tags")
 *   a connection schema                → its own line, named for what it is
 *   a capability                       → its own line, in humanizeDlr's words
 */
const PROSE_VERBS = ['read', 'create', 'edit', 'delete', 'propose'];
const andList = (xs) => (xs.length < 2 ? xs.join('')
  : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
/* A person has ONE profile; every other schema is a kind of thing they have many of. */
const SINGULAR = new Set(['profile']);
const nounOf = (schema) => {
  const s = String(schema || '').replace(/_/g, ' ');
  if (SINGULAR.has(schema)) return s;
  // A schema name is whatever an app called it, so this only handles the
  // regular endings: -y → -ies, a sibilant (push, match, box) takes -es, and a
  // name already ending in s is left as the plural it probably is.
  if (/[^aeiou]y$/.test(s)) return s.replace(/y$/, 'ies');
  if (/(sh|ch|x|z)$/.test(s)) return `${s}es`;
  return /s$/.test(s) ? s : `${s}s`;
};
const verbsOf = (grants) => {
  const vs = new Set(grants.map((g) => g.verb));
  const ordered = PROSE_VERBS.filter((v) => vs.has(v))
    .map((v) => (v === 'propose' ? 'suggest changes to' : v));
  const text = andList(ordered);
  return text ? text[0].toUpperCase() + text.slice(1) : '';
};
const CAP_RESOURCE = { discovery: 'discovery', activity: 'activity', plugins: 'plugins',
                       automations: 'automations', sub_entity: 'sub_entity' };

/** A type's name, with its face, its owner and its description behind it.
 *
 *  THE MARK CAME OFF THE LINE. It was drawn inline for a while — leading the
 *  name, then trailing it, at four sizes and three alignments — and both
 *  positions interrupt the sentence they sit in. `TypeName` keeps all of it
 *  behind the word instead; see that file for the argument.
 *
 *  Everything that says "this type" still says it with this one component, so
 *  the prose and the table cannot drift apart. */
const Typed = ({ face, children }) => <TypeName face={face}>{children}</TypeName>;

/** `andList` for nodes rather than strings — the same commas and the same final
 *  "and", around names that now carry a tile each. */
const andNodes = (xs) => xs.map((x, i) => (
  <React.Fragment key={i}>
    {i > 0 ? (i === xs.length - 1 ? ' and ' : ', ') : null}
    {x}
  </React.Fragment>
));

export function proseLines(grants = [], items = [], owner = 'your', faceOf = null) {
  /* The face of the type a group of rows is about. The rows in a group are all
     one schema, so the first that names one answers for them. */
  const faceFor = (gs) => (faceOf
    ? faceOf((gs.find((g) => g.schema_id) || {}).schema_id) : null);
  const nodes = grants.filter((g) => g.resource === 'nodes');
  const lines = [];
  // ALL OF YOUR DATA IS NOT A TYPE, so it does not get a type's line.
  //
  // It read "Read everything in your identity graph" as one bullet among
  // "your goals" and "your habits" — the same shape, the same weight, in the
  // same list — so the widest ask on the page looked like one more schema
  // called "everything". It is not a schema: it is a statement about the ask's
  // BREADTH, it covers types that do not exist yet, and what it finally reaches
  // is decided by the realms below rather than by any list. `kind: 'all'` is
  // what lets AskProse lift it out of the list and say so.
  const wide = nodes.filter((g) => g.all_schemas);
  if (wide.length) lines.push({ key: 'all', kind: 'all', grants: wide,
    text: <>{verbsOf(wide)} <b>all of {owner} data</b></> });
  // Plain types, grouped by the exact verbs asked of them, in first-seen order.
  const bySchema = new Map();
  nodes.filter((g) => !g.all_schemas && !g.connects_nodes).forEach((g) => {
    const k = g.schema_name || g.schema_id;
    bySchema.set(k, [...(bySchema.get(k) || []), g]);
  });
  const byVerbs = new Map();
  bySchema.forEach((gs, schema) => {
    const k = verbsOf(gs);
    byVerbs.set(k, [...(byVerbs.get(k) || []), [schema, gs]]);
  });
  byVerbs.forEach((pairs, verbs) => lines.push({
    key: `t:${pairs.map(([s]) => s).join(',')}`,
    grants: pairs.flatMap(([, gs]) => gs),
    // EVERY NAME WEARS ITS OWN FACE, including the two or three that share a
    // line because they share their verbs. One tile in front of "notes and
    // tasks" would be a face for the pair, which is not a thing.
    text: <>{verbs} {owner} <b>{andNodes(pairs.map(([sch, gs]) => (
      <Typed face={faceFor(gs)} key={sch}>{nounOf(sch)}</Typed>
    )))}</b></>,
  }));
  // Connections, one line each: they are governed by rules of their own.
  const links = new Map();
  nodes.filter((g) => !g.all_schemas && g.connects_nodes).forEach((g) => {
    const k = g.schema_name || g.schema_id;
    links.set(k, [...(links.get(k) || []), g]);
  });
  links.forEach((gs, schema) => lines.push({
    key: `c:${schema}`, grants: gs,
    // The audience line names no type — it is about where a node already sits —
    // so there is no face to put on it.
    text: gs[0].inherits_audience
      ? <>{verbsOf(gs)} what {owner} nodes <b>belong to</b></>
      : (<>{verbsOf(gs)} {owner} <b><Typed face={faceFor(gs)}>
          {schema === 'relationship' ? 'relationships' : nounOf(schema)}
        </Typed></b></>),
  }));
  // Capabilities: humanizeDlr's sentence, with only its object in bold.
  const { capabilities = [] } = categorizeDlr(items || [], [], null, {});
  capabilities.forEach((c) => {
    const words = String(c.text).split(' ');
    const last = words.pop();
    lines.push({
      key: `cap:${c.kind}:${c.text}`,
      grants: grants.filter((g) => g.resource === CAP_RESOURCE[c.kind]),
      text: <>{words.join(' ')} <b>{last}</b></>,
    });
  });
  return lines;
}

/** Does a realm, on its own, reach any of this line? From its `missing`: a realm
 *  that gives read on your goals but not edit still answers part of the line. */
export const realmCovers = (realm, line) => !!realm && line.grants.length > 0
  && line.grants.some((g) => !(realm.missing || []).some((m) =>
    m.resource === g.resource && m.verb === g.verb
    && String(m.schema_id ?? '') === String(g.schema_id ?? '')));

/* WHOSE DATA, said on every line. Acting for a group, the entries are the
   group's, and "your goals" would tell an admin they were exposing themselves. */
/** "new" — a row the grant you already hold does not cover. One component, so
 *  the prose and the table can never mark the same fact two different ways. */
export const NewMark = () => (
  <span className="gp-new" title="Not in the grant you already hold">new</span>
);

export function AskProse({ grants = [], items = [], available, decided = true, previewRealm,
                           owner = 'your', asksLinks = false, linksOn = false, isNew,
                           faceOf }) {
  const all = proseLines(grants, items, owner, faceOf);
  const wide = all.find((l) => l.kind === 'all') || null;
  const lines = all.filter((l) => l !== wide);
  if (asksLinks) {
    // THE SHAPE IS ITS OWN LINE, and it is the one line whose answer does not
    // come from a grant row — `include_refs` rides on the read scope, so there
    // is no row for `available` to judge. Its mark reads the realms directly.
    lines.push({ key: 'refs', kind: 'refs', grants: [], met: linksOn,
      text: <>See <b>how {owner} data connects</b> — the links between entries</> });
  }
  if (!wide && !lines.length) return null;
  const wideMet = !wide || wide.grants.every((g) => (available ? available(g) : true));
  return (
    <div data-testid="ask-prose">
    {wide && (
      /* THE BREADTH, STATED BEFORE THE LIST AND NOT IN IT. Same gutter, so the
         ✓/× column still runs straight down the page; its own weight, because
         "all of your data" is the sentence the whole rest of the page qualifies. */
      <div className={`gp-allask${decided && !wideMet ? ' is-short-row' : ''}`}
           data-testid="ask-all">
        <span className={`gp-want__mark${decided ? (wideMet ? ' is-met' : ' is-short') : ''}`}
              role={decided ? 'img' : undefined}
              aria-label={decided ? (wideMet ? 'covered by the realms you chose'
                : 'not covered by the realms you chose') : undefined}>
          {!decided ? '' : (wideMet ? '✓' : '×')}
        </span>
        <span className="gp-allask__t">
          {wide.text}
          {isNew && wide.grants.some(isNew) && <NewMark />}
        </span>
      </div>
    )}
    {wide && lines.length > 0 && <p className="gp-wants__lab">It also asks to</p>}
    {lines.length > 0 && (
    <ul className="gp-wants">
      {lines.map((l) => {
        // A line carrying its own verdict keeps it: the links line has no grant
        // row to judge, and "no rows" must not read as "satisfied" for it the
        // way it correctly does for a capability sentence.
        const met = l.met !== undefined
          ? l.met
          : (l.grants.length === 0
            || l.grants.every((g) => (available ? available(g) : true)));
        const mark = !decided ? '' : (met ? '✓' : '×');
        return (
          <li key={l.key}
              className={`gp-want${realmCovers(previewRealm, l) ? ' is-preview' : ''}`}>
            <span className={`gp-want__mark${decided ? (met ? ' is-met' : ' is-short') : ''}`}
                  role={decided ? 'img' : undefined}
                  aria-label={decided ? (met ? 'covered by the realms you chose'
                    : 'not covered by the realms you chose') : undefined}>{mark}</span>
            {/* MARKED HERE TOO. The banner's "N new, marked below" is not a
                claim about the table view — the prose is what opens, so it is
                the first place the promise has to be kept. */}
            <span className="gp-want__t">
              {l.text}
              {isNew && l.grants.some(isNew) && <NewMark />}
              {/* WHY, IN THE APP'S OWN WORDS. A runtime ask carries a reason
                  per row — "To show github_push activity as evidence on your
                  Ship v2 mission" — and it is the sentence that turns "some
                  app wants your data" into a thing the person asked for.
                  Under the line, not in it: the line says WHAT, in the page's
                  voice; this says why, in the app's. Distinct reasons only —
                  a batch that gives four rows one sentence says it once. */}
              {[...new Set(l.grants.map((g) => g.reason).filter(Boolean))].map((why) => (
                <span key={why} className="gp-want__why">{why}</span>
              ))}
            </span>
          </li>
        );
      })}
    </ul>
    )}
    </div>
  );
}

/** The verbs asked for that the chosen realms do not reach. */
export function withheldVerbs(grants, isOn, available) {
  return KEY_ORDER.filter((v) => verbStates(grants, isOn, available).get(v)?.out);
}

export function VerbKey({ grants = [], isOn = () => true, available }) {
  const asked = verbStates(grants, isOn, available);
  if (!asked.size) return null;

  return (
    <span className="gp-key" role="img"
          aria-label={KEY_ORDER.filter((v) => asked.has(v))
            .map((v) => {
              const st = asked.get(v);
              return `${v}: ${st.out ? 'not reachable from the chosen realms'
                : st.on ? 'granted' : 'asked for'}`;
            })
            .join(', ') || 'no permissions'}>
      {KEY_ORDER.map((v) => {
        // A TAG MEANS ASKED FOR — not granted.
        //
        // Tying the box to fulfilment made the ask itself flicker: before a
        // realm was chosen nothing was reachable, so a page showing what an app
        // REQUIRES rendered every verb as if it were absent, then boxes appeared
        // as realms were ticked. The requirement never changed; only the answer
        // did. It also moved the rows around under the reader's cursor.
        //
        // So the box states the request, which is fixed, and the row's × states
        // the shortfall, which is not. Same rule the capability tags already
        // follow — their tags do not change when a realm cannot reach them.
        const st = asked.get(v);
        // A box means asked; a FILLED box means asked and reached by the realms
        // you chose — the ask turning into a grant, one verb at a time.
        const cls = !st ? 'is-na' : (st.out ? 'is-on is-out' : 'is-on is-got');
        return (
          <span key={v} className={`gp-key__slot ${cls}`} title={
            !st ? `${v} — not requested`
              : st.out ? `${v} — asked for, but the chosen realms do not reach it`
                : `${v} — granted by the realms you chose`}>
            {v}
          </span>
        );
      })}
    </span>
  );
}

export function Scope({ verbs = [], value = 'any', onChange, consequence,
                       wideLabel = 'Anything of yours', appName }) {   // eslint-disable-line no-unused-vars
  if (!verbs.length) return null;
  // A CHECKBOX, AT THE WEIGHT OF THE ROWS IT QUALIFIES. The segmented pair was
  // a boxed panel with a title, a verb list and a consequence line — four
  // pieces of chrome around one binary, sitting louder than the rows it
  // qualifies.
  //
  // STATED AS THE RESTRICTION, SO THE BOX IS EMPTY BY DEFAULT.
  //
  // It used to read "Let this app have access to nodes it didn't create" and
  // ship CHECKED, because `any` is what the grant sends and a box drawn empty
  // over a grant that sends `any` would show a narrower grant than it submits.
  // Correct, and still the constraint — but it made the one control on the page
  // arrive pre-ticked, which reads as something already agreed rather than
  // something to decide.
  //
  // Inverting the sentence resolves both at once: unchecked now MEANS `any`, so
  // an empty box is exactly what is sent, and ticking it is the narrowing act it
  // always was. The invariant is unchanged — the box's state must match the
  // submitted scope — it is the wording that stopped fighting it.
  //
  // THE VERBS ARE THE SENTENCE. "Restrict this app" never said what the
  // restriction touches; naming the scoped verbs does, in the order a person
  // reads them — and `verbs` still decides whether the question is asked at all.
  const restricted = value === 'own';
  return (
    <label className="gp-opt">
      <input
        type="checkbox"
        checked={restricted}
        onChange={() => onChange?.(restricted ? 'any' : 'own')}
      />
      <span>
        {`Only let ${appName || 'this app'} ${andList(PROSE_VERBS.filter((v) => verbs.includes(v)))} nodes it created`}
        {/* KEPT, against the design's single line. The original shipped control
            and consequence as ONE component on purpose — "the control is
            meaningless without the line saying what flipping it costs" — and a
            restyle must not quietly take that back. It moves INSIDE the label,
            so the checkbox still reads as one thing.

            It also resolves what the segmented pair got wrong: "Anything of
            yours" is false when acting for a group, and this wording never
            claims ownership at all. */}
        {/* A caller's consequence travels with the control in either state; with
            none, ticking it still says what the restriction leaves possible. */}
        {(consequence || restricted) && (
          <em className="gp-opt__says">
            {consequence || 'It can still create new ones. Everything else of yours stays out of reach.'}
          </em>
        )}
      </span>
    </label>
  );
}

/** G2 — one card per schema, its verbs as chips. Used when the bucket collapsed
 *  on the schema axis. Card tone follows the most consequential verb that is
 *  still ON, so the card calms as the user declines. */
export function SchemaCard({ card, count, editable, isOn, available, onToggle,
                             pickedTypes, onToggleType, decided = true, blockedBy,
                             isNew, faceOf }) {
  const live = card.grants.filter(isOn);
  const face = faceOf ? faceOf((card.grants.find((g) => g.schema_id) || {}).schema_id) : null;
  const lead = live.length
    ? live.reduce((a, g) => (metaOf(g).sev < metaOf(a).sev ? g : a))
    : null;
  const tone = lead ? metaOf(lead).tone : 'read';
  const withheld = withheldVerbs(card.grants, isOn, available);
  // THE CAUSE, ON THE ROW'S OWN MARK. The × said which verbs fell out but never
  // why, so a row blocked because the realm lacks `delete` and a row blocked
  // because the realm does not hold that type were the same mark with the same
  // words. Read off the same `blockedBy` the list under the realms uses, so the
  // two surfaces cannot give a row two different reasons.
  //
  // IT STAYS IN THE TITLE rather than becoming a second line. A "not covered"
  // line under the row was removed once already: it grew the row the moment a
  // realm was ticked and shifted the whole list mid-decision. The itemised
  // version lives under the verdict, where nothing is competing for the gutter.
  const causes = blockedBy
    ? [...new Set(card.grants.map((g) => blockedBy(g)).filter(Boolean))]
    : [];
  const why = causes.length !== 1 ? null : {
    verb: 'the realms you chose do not allow it',
    schema: 'the realms you chose do not include this type',
    other: 'the realms you chose narrow it',
    mixed: 'no single realm you chose covers it',
  }[causes[0]];
  return (
    <div className={`gp-card gp-tone-${tone} gp-card--${card.schemaless ? 'cap' : 'schema'}`}
         data-testid="schema-card">
      {/* NO ENTRY COUNT. "47 entries" is a number the page cannot stand behind
          at the moment it shows it: nothing is granted until a realm is chosen
          below, so it describes what you HAVE, not what this app would get. */}
      {/* MARKED BELOW, AS THE BANNER SAYS. On a re-authorization the why line
          reads "N new, marked below" — and until now nothing was: `wants_more`
          was read for its length alone. A row the prior grant did not hold is
          the only thing on this page that changed since you last agreed, so it
          is the only thing that needs pointing at. */}
      <span className="gp-card__name">
        <Typed face={face}>{card.label}</Typed>
        {isNew && card.grants.some(isNew) && <NewMark />}
      </span>
      <span className="gp-card__key">
        <VerbKey grants={card.grants} isOn={isOn} available={available} />
      </span>
      {/* THE ROW'S VERDICT, at the ROW's right edge rather than tucked against
          the verbs — so one column of × runs down the page instead of two
          ragged ones. It appears only once a realm is chosen: before that there
          is no shortfall, only an unanswered question.

          THE SLOT IS ALWAYS THERE, the mark is not. Rendering it conditionally
          changed the row's width the moment a realm was ticked, and the list
          shifted under the cursor mid-decision. */}
      {/* BOTH ANSWERS, NOT JUST THE BAD ONE. Marking only shortfalls left every
          satisfied row silent, so "covered" and "not yet decided" looked the
          same — an empty gutter meant either. A ✓ says the row is settled. */}
      <span className={`gp-card__short${decided ? (withheld.length ? ' is-short' : ' is-met') : ''}`}
            role={decided ? 'img' : undefined}
            aria-hidden={decided ? undefined : 'true'}
            aria-label={!decided ? undefined : withheld.length
              ? `not covered: ${withheld.join(', ')}${why ? ` — ${why}` : ''}`
              : 'covered by the realms you chose'}
            title={!decided ? undefined : withheld.length
              ? `Not covered: ${withheld.join(', ')}${why ? `\n\nWhy: ${why}.` : ''}`
              : 'Covered by the realms you chose'}>
        {decided ? (withheld.length ? '×' : '✓') : ''}
      </span>
      {/* The row's second line: anything that QUALIFIES the grant rather than
          stating it. Full width, so it never competes with the verbs for the
          right edge. */}
      <div className="gp-card__extra">
      {/* NO "not covered" LINE. The row's × says it, and this said it again
          fifteen pixels lower — which also grew the row the moment a realm was
          ticked, so the whole list shifted while you were choosing. The verbs
          it named are on the ×'s title and in the verdict under the realms. */}
      {/* EACH CONNECTOR ROW STATES ITS OWN RULE.
          It used to be one sentence over the group, which meant the row that
          behaves differently — belongs_to, the only edge that WIDENS — was
          covered by a note describing the other one. A rule that is not the
          same for every row it sits above is not a group note.

          Per row it also lands where the reader is looking when they decide,
          instead of several rows earlier. */}
      {card.connectsNodes && (
        <p className="gp-caveat">
          {/* PARALLEL ON PURPOSE. Both rows open the same way, so the two are
              comparable at a glance and the only thing that stands out is where
              they differ. Verb-independent by construction: this states when a
              connection of this kind is shared, which does not change with the
              verb the app was granted.

              "Connection is" is dropped from both: the row already names the
              schema, so the subject was doing no work — and the full sentence
              wrapped to two lines inside the width the verb columns leave. */}
          {card.inheritsAudience
            ? <>Shared only when the &lsquo;to&rsquo; node is shared</>
            : <>Shared when the nodes it connects are shared</>}
        </p>
      )}
      {/* Under the chips: the verb is the question, the kinds are a refinement
          of it. Shown only for the grants that actually carry options — the
          server omits them for `belongs_to`, which has no type by design. */}
      {/* SHOWN WHENEVER THE ASK CARRIES OPTIONS, not only once a realm reaches
          the grant. Gating it on `isOn` made the control appear the moment a
          realm was ticked, which grew the row by 21px and shifted every row
          below it — the realm list moving under the cursor as you chose. The
          narrowing is a decision about the ASK, and the ask is there from the
          start. */}
      {card.grants.filter((g) => g.filter_options).map((g) => (
        Object.entries(g.filter_options).map(([field, options]) => (
          <TypeFilter
            key={`${g.verb}:${field}`}
            schemaId={g.schema_id}
            field={field}
            options={options}
            counts={g.filter_option_counts?.[field]}
            selected={pickedTypes ? pickedTypes(g.schema_id, field) : []}
            editable={editable}
            onToggle={onToggleType}
          />
        ))
      ))}
      </div>
    </div>
  );
}

/** The unrestricted nodes ask, in the table — a BREADTH, not a row in the type list.
 *
 *  Collapsed on the schema axis, a schemaless nodes grant becomes a card keyed
 *  `res:nodes` and labelled "Everything", which then sorts in among `goal`,
 *  `habit` and `journal_entry` wearing their shape. Every signal said type: the
 *  same row, the same mono face, the same column of verbs. The one row that
 *  covers all the others — and types nobody has created yet — read as one more
 *  of them.
 *
 *  So it leaves the list and leads the section, saying the two things a type row
 *  never has to: the realms decide how far it reaches, and it does not stop at
 *  today's types. The verb key and the ✓/× gutter are unchanged, because the
 *  answer to it is read the same way as the answer to any other line.
 */
export function AllData({ cards = [], isOn, available, decided = true, owner = 'your' }) {
  if (!cards.length) return null;
  const grants = cards.flatMap((c) => c.grants);
  const withheld = withheldVerbs(grants, isOn, available);
  return (
    <div className="gp-allrow" data-testid="ask-all-row">
      <span className={`gp-allrow__short${decided ? (withheld.length ? ' is-short' : ' is-met') : ''}`}
            role={decided ? 'img' : undefined}
            aria-hidden={decided ? undefined : 'true'}
            aria-label={!decided ? undefined : withheld.length
              ? `not covered by the realms you chose: ${withheld.join(', ')}`
              : 'covered by the realms you chose'}>
        {decided ? (withheld.length ? '×' : '✓') : ''}
      </span>
      <span className="gp-allrow__name">All of {owner} data</span>
      <span className="gp-allrow__key">
        <VerbKey grants={grants} isOn={isOn} available={available} />
      </span>
      {/* NO EXPLANATORY LINES HERE. The table is the terse reading of the same
          ask — a row, its verbs, its verdict — and prose is where the sentences
          live. The breadth is carried by the row's PLACE (above the types,
          outside their list) and its name, not by a paragraph restating in the
          table what the other view already says. */}
    </div>
  );
}

/** Non-node permissions, as sentences.
 *
 *  `Sub-entities / DECLARE` names a resource and a verb; "Declare sub-entities"
 *  says what it does. The wording comes from `humanizeDlr`, which the product
 *  already uses elsewhere — a second set invented here is how an interface
 *  stops being one interface.
 */
/** Every tool an ask sets the tool access of, in its words' order (Allowed, Asks you,
 *  Never): [{ pluginId, toolId, mode }]. A later item naming a tool again does not
 *  widen it: the narrower mode stands. */
const MODE_RANK = { allow: 0, ask: 1, deny: 2 };
export function askedTools(items = []) {
  const by = new Map();
  for (const it of items) {
    if (it?.resource !== 'plugins' || (it.verb && it.verb !== 'execute') || !it.tools || typeof it.tools !== 'object') continue;
    for (const [pid, modes] of Object.entries(it.tools)) {
      if (!modes || typeof modes !== 'object') continue;
      for (const [tid, mode] of Object.entries(modes)) {
        if (!(mode in MODE_RANK)) continue;
        const key = `${pid}:${tid}`;
        const was = by.get(key);
        if (!was || MODE_RANK[mode] > MODE_RANK[was.mode]) by.set(key, { pluginId: String(pid), toolId: String(tid), mode });
      }
    }
  }
  return [...by.values()].sort((a, b) => MODE_RANK[a.mode] - MODE_RANK[b.mode]);
}

export function Capabilities({ items = [], grants = [], isOn, decided = true }) {
  const { capabilities } = categorizeDlr(items, [], null, {});
  // TOOL ACCESS, tool by tool: each named by its plugin and its own name
  // ("Mailroom · Send email"), what it reads and writes, and its mode — read
  // from the plugin list and the tools listing only when an ask names a tool
  const tools = askedTools(items);
  const names = usePluginNames(tools.length > 0);
  const facts = useToolFacts(tools.length > 0);
  if (!capabilities?.length) return null;

  // A CAPABILITY ANSWERS TO THE REALM TOO. These were rendered from the DLR
  // alone, so they read as granted whatever realm was chosen — every other row
  // on the page dims when the realm cannot reach it, and these quietly did not.
  // `kind` maps to the resource the capability came from.
  const RESOURCE_OF = { discovery: 'discovery', activity: 'activity',
                        plugins: 'plugins', automations: 'automations',
                        sub_entity: 'sub_entity' };
  const granted = (kind) => {
    const resource = RESOURCE_OF[kind];
    if (!resource || !isOn) return true;
    const mine = grants.filter((g) => g.resource === resource);
    return mine.length ? mine.some(isOn) : true;
  };

  return (
    <ul className="gp-caps">
      {capabilities.map((c) => (
        <li key={c.kind + c.text}
            className={`gp-cap${granted(c.kind) ? '' : ' is-off'}`}>
          <span className="gp-cap__text">{c.text}</span>
          {!(c.kind === 'plugins' && tools.length) && c.detailTags?.length > 0 && (
            <CapTags tags={c.detailTags}
                     decided={decided}
                     noun={c.kind === 'plugins' ? 'plugin'
                       : c.kind === 'activity' ? 'category' : 'kind'}
                     stateOf={c.kind === 'discovery' ? (t) => {
                       // the tag IS the verb on a discovery grant
                       const g = grants.find((x) => x.resource === 'discovery'
                         && x.verb === String(t).toLowerCase());
                       return g ? (isOn?.(g) ?? true) : null;
                     } : undefined} />
          )}
          {/* PER-TOOL LIMITS, SAID OUT LOUD.
              A plugin is not a capability: Google is calendar, contacts, mail,
              files and watch history. An ask can narrow by tool and the backend
              enforces it — but this screen said nothing, so you were consenting
              to a shape you could not see. Enforced and invisible is the worst
              of the three ways this goes wrong, because the system looks like it
              is still describing itself and has quietly stopped.

              Every tool the ask names appears, with its mode — Allowed, Asks
              you, Never — because giving an app a tool is all it needs: the
              tool then reads and writes what it asks for (plugin-words.md,
              "The rule"). So each says what it reads and writes too. */}
          {c.kind === 'plugins' && tools.length > 0 && (
            <ul className="gp-cap__tools">
              {tools.map((t) => {
                const line = toolAsksLine(facts[`${t.pluginId}:${t.toolId}`] || {});
                return (
                  <li key={`${t.pluginId}_${t.toolId}`}
                      className={`gp-cap__tool gp-cap__tool--${t.mode}`}>
                    <span className="gp-cap__tool-what">
                      <span className="gp-cap__tool-name">{toolNameOf(names, t.pluginId, t.toolId)}</span>
                      {line && <span className="gp-cap__tool-asks">{line}</span>}
                    </span>
                    <span className="gp-cap__tool-mode">
                      {accessWord(t.mode)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {/* THE SAME MARK AS A SCHEMA ROW. A capability the chosen realms
              cannot reach is the same fact as an uncovered verb, so it is told
              the same way — the × at the row's right edge, not a strike through
              the words. */}
          {decided && (
            <span className={`gp-cap__short${granted(c.kind) ? ' is-met' : ''}`} role="img"
                  aria-label={granted(c.kind) ? 'covered by the realms you chose'
                    : 'not covered by the realms you chose'}
                  title={granted(c.kind) ? 'Covered by the realms you chose'
                    : 'Not covered by the realms you chose'}>
              {granted(c.kind) ? '✓' : '×'}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The row-filter control: WHICH kinds, not which fields.
 *
 * A relationship is one schema covering every kind of link, so ticking the
 * schema is an all-or-nothing choice over a category that has real internal
 * variety. `relationship_type` is the axis a person actually reasons about —
 * and was a first-class scope until rels-as-nodes folded it away.
 *
 * Chips rather than the shared `FilterBuilder`: that component exists to author
 * an arbitrary predicate (pick a field, pick an operator, type a value), which
 * is right for the realm editor where the schema is unknown up front. Here the
 * field and the operator are both fixed and the values are enumerated by the
 * server, so a builder would ask three questions to collect one answer.
 *
 * NOTHING SELECTED MEANS EVERYTHING OFFERED. Stated on screen, because an empty
 * multi-select otherwise reads as "none" — and the two are opposite grants.
 */
function TypeFilter({ schemaId, field, options, counts, selected, editable, onToggle }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [at, setAt] = useState(null);
  const box = useRef(null);
  const panel = useRef(null);
  // The panel is not a descendant any more (see below), so "outside" has to mean
  // outside BOTH or every click inside the menu would close it.
  useClickOutside(box, (e) => {
    if (!panel.current?.contains(e.target)) setOpen(false);
  }, open);

  // A MENU IS NOT A ROW. Rendered in flow it grew the row it belongs to, pushed
  // every row under it down, and then got clipped by the capped list — a floating
  // layer behaving like content because it happened to live inside some. It is
  // portalled to the page and positioned from the trigger instead.
  //
  // INTO `.gp-route`, NOT THE BODY. This page's palette is scoped to that
  // element, so a body-appended layer loses every token and paints itself
  // invisible — white on white with no border.
  useLayoutEffect(() => {
    if (!open) { setAt(null); return undefined; }
    const place = () => {
      const r = box.current?.getBoundingClientRect();
      if (r) setAt({ top: r.bottom + 4, left: r.left });
    };
    place();
    // `fixed` is relative to the viewport, so anything that moves the trigger —
    // the page scrolling, the list scrolling, a resize — has to re-place it.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // THE PLATFORM'S OWN NOUN. relationship.json titles this field "Type"; calling
  // it anything else here would be a second vocabulary for one thing.
  const label = field === 'relationship_type' ? 'types' : field;
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    const sel = new Set(selected);
    // Chosen first and never filtered out: the selection is the thing being
    // built, so a row vanishing mid-search reads as having lost it.
    return [...options.filter((o) => sel.has(o)),
            ...options.filter((o) => !sel.has(o)
              && (!term || o.toLowerCase().includes(term)))];
  }, [options, selected, q]);

  if (!options?.length) return null;
  const none = !selected.length;

  return (
    <div className="gp-narrow" ref={box}>
      {/* COLLAPSED BY DEFAULT. Most people want every kind, and rendering the
          whole vocabulary inline shouted a refinement most grants never make —
          on every card, at every size. The trigger states the current grant, so
          the closed form is still an answer, not just a way in. */}
      <button
        type="button"
        className={`gp-narrow__trigger${none ? '' : ' is-set'}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={!editable}
        onClick={() => setOpen((v) => !v)}
      >
        {/* IT HAS TO LOOK LIKE A FILTER, AND SAY NOTHING WHEN IT IS NOT ONE.
            "all 3" was a value printed for a control nobody had touched — it
            annotated the default, which is the state that needs no annotation.
            The count now appears only once a filter is actually applied, so
            seeing one MEANS something is narrowed. The caret carries the rest:
            an affordance, not a statement. */}
        {/* NAME THE CHOICE, DO NOT COUNT IT. "1 of 3" made the reader hold two
            numbers and derive a fact — and the fact they want is WHICH type,
            which the count cannot give at any length. Naming it is shorter and
            says more; past two, the tail folds so one long vocabulary cannot
            push the row around. */}
        <span className="gp-narrow__label">
          {none ? `Filter by ${label.replace(/s$/, '')}` : `Only ${label.replace(/s$/, '')}`}
        </span>
        {!none && (
          <span className="gp-narrow__val-sum">
            {selected.slice(0, 2).join(', ')}
            {selected.length > 2 && ` +${selected.length - 2}`}
          </span>
        )}
        {/* THE CARET TRAILS THE PHRASE, like the select beside it. Leading, at
            8px, it read as a bullet — a mark before a line of prose rather than
            a control you can operate. After the words and at their own size, it
            is the affordance. */}
        <span className="gp-narrow__caret" aria-hidden="true">▾</span>
      </button>

      {open && at && createPortal((
        <div className="gp-narrow__panel" role="listbox" aria-multiselectable="true"
             ref={panel} style={{ top: `${at.top}px`, left: `${at.left}px` }}>
          {options.length > 8 && (
            <input
              className="gp-narrow__search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Filter ${options.length} ${label}`}
              aria-label={`Filter ${field} values`}
            />
          )}
          <div className="gp-narrow__list">
            {matches.map((opt) => {
              const on = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`gp-narrow__opt${on ? ' is-on' : ''}`}
                  onClick={() => onToggle?.(schemaId, field, opt)}
                >
                  <span className="gp-narrow__tick" aria-hidden="true" />
                  <span className="gp-narrow__val">{opt}</span>
                  {/* A value you hold none of is SHOWN, not hidden: "you may
                      authorise this, you just have none yet" is a different
                      statement from "this does not exist". */}
                  {counts && (
                    <span className="gp-narrow__n">
                      {counts[opt] ? fmt(counts[opt]) : 'none yet'}
                    </span>
                  )}
                </button>
              );
            })}
            {!matches.length && (
              <p className="gp-narrow__empty">No {label.replace(/s$/, '')} matches “{q}”.</p>
            )}
          </div>
          {/* Says what an empty selection MEANS. Blank multi-selects read as
              "none", and none vs all are opposite grants. */}
          <p className="gp-narrow__foot">
            {none
              ? `Nothing picked — the app gets all ${options.length} ${label}.`
              : (selected.length === 1
                ? 'The app gets only this one.'
                : `The app gets only these ${selected.length}.`)}
            {!none && (
              <button type="button" className="gp-narrow__clear"
                      onClick={() => selected.forEach((v) => onToggle?.(schemaId, field, v))}>
                Reset
              </button>
            )}
          </p>
        </div>
      ), document.querySelector('.gp-route') || document.body)}
    </div>
  );
}

/**
 * A capability's specifics — plugin names, activity categories, discovery kinds.
 *
 * INLINE WHILE SHORT, BENEATH ONCE IT IS NOT. Two tags sit happily beside the
 * sentence; a dozen plugin names wrap into a ragged block that pushes the row
 * open and buries the sentence it belongs to. Past the threshold they move to
 * their own line, where wrapping is expected and the label column stays clean.
 *
 * FOLDED, NOT HIDDEN — and folded with a `+N more`, not a dropdown. The type
 * filter beside it opens a panel because it is a CONTROL: you are choosing.
 * These are FACTS about the ask; you cannot decline one plugin. Giving them the
 * same affordance would promise a choice that does not exist.
 *
 * `plugin_names` is unbounded and falls back to raw plugin_ids (see
 * humanizeDlr) — so this has to survive both a long list and one made of UUIDs.
 */
function CapTags({ tags = [], noun = 'item', stateOf, decided }) {
  const [open, setOpen] = useState(false);
  // ONE TAG STAYS INLINE; A LIST FOLDS. A single value (`friends`) costs less to
  // read than a click; two or more fold behind `see info`, so a long plugin list
  // never decides the row's height.
  const INLINE = 1;

  /* A MARK PER OPTION, WHERE THE DATA SUPPORTS ONE.
   *
   * `discovery` arrives as one grant PER KIND — discovery:friends and
   * discovery:groups are separate rows in the ask — so each can honestly say
   * whether the chosen realms reach it.
   *
   * `plugins` and `activity` cannot. Each is a SINGLE grant (plugins:execute,
   * activity:read) whose per-item scope lives in `plugin_ids` / `categories`
   * inside the access blob — lists the page never receives. A realm may narrow
   * them (see _LIST_FIELDS in authz/access.py), so marking every plugin with
   * the row's own state would claim coverage this page cannot check. They are
   * listed without marks until the backend can say which ids survive the realm.
   */
  // ONE SLOT, THREE STATES. Every row leads with the same 9px column, so the
  // names start on one edge whatever is known about them:
  //   •   listed, no verdict — either nothing is decided yet, or (plugins,
  //       activity categories) the page has no per-item answer to give
  //   ✓/× the realms you chose do / do not reach this one
  // A bullet that becomes a verdict in place reads as the same list gaining
  // information. A bullet PLUS a mark would be two glyphs saying one thing.
  const mark = (t) => {
    const st = decided && stateOf ? stateOf(t) : null;
    if (st == null) {
      return <span className="gp-cap__optmark is-bullet" aria-hidden="true">&middot;</span>;
    }
    return (
      <span className={`gp-cap__optmark${st ? ' is-met' : ''}`} aria-hidden="true">
        {st ? '✓' : '×'}
      </span>
    );
  };
  // THE MARK SITS OUTSIDE THE TAG, not inside it. A bordered chip carrying an
  // × is the universal "remove this filter" control, so an × inside the border
  // reads as a button that deletes the option rather than a verdict about it —
  // the same misread that moved the row mark out to the gutter. Outside, it is
  // an annotation, which is what every other mark on this page is.
  const tag = (t) => <span className="gp-cap__opt" key={t}>{mark(t)}<em>{t}</em></span>;

  // A SHORT LIST IS ITS OWN SUMMARY. Two names cost less to read than a count
  // and a click, so they stay in place beside the sentence.
  if (tags.length <= INLINE) {
    return <span className="gp-cap__tags">{tags.map(tag)}</span>;
  }

  // A LONG ONE COLLAPSES TO ITS COUNT. Showing four of eight put a block of
  // cards under a one-line sentence and still hid half of them — the worst of
  // both: it took the space of a list without being one. The count is the fact
  // the reader needs first ("how much am I agreeing to run?"); the names are
  // one click away for the reader who needs them, and they DO need to be
  // available — a plugin is third-party code, not a category.
  //
  // THE TOGGLE DOES NOT MOVE WHEN YOU PRESS IT. It used to live inside the tag
  // span, which goes full-width when stacked — so opening the list carried the
  // button down to the end of it, and closing again meant hunting for where the
  // control had gone. One press should be undoable by a second press in the
  // same place. So the button is a sibling of the tags, not a member of them:
  // it stays beside the sentence, and only the names drop to their own line.
  return (
    <>
      <button type="button" className="gp-cap__more"
              aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'collapse' : 'see info'}
      </button>
      {open && (
        // A SCROLLING LIST, NOT PILLS. Two problems with the pills, and the
        // second is the one that mattered: wrapping made the row's height a
        // function of how many plugins the app asked for, and laying them out
        // sideways instead only traded that for names clipped mid-word, read
        // in the one direction prose is not read in.
        //
        // A list is what this is — third-party code, one item per line, name
        // legible in full and the marks in a column you can run an eye down.
        // Capped in height, so twenty plugins cost the same page as four.
        //
        // tabIndex makes it reachable: a region that only scrolls horizontally
        // is unusable from the keyboard unless it can take focus.
        <span className="gp-cap__tags is-stacked" tabIndex={0}
              role="group" aria-label={`${tags.length} ${noun}s`}>
          {tags.map(tag)}
        </span>
      )}
    </>
  );
}

/**
 * The whole type list, capped once it is taller than the decision below it.
 *
 * THE CAP LANDS BETWEEN ROWS, NEVER THROUGH ONE. A height in `vh` cuts wherever
 * the window happens to end, and a row sliced through its middle does not read
 * as "there is more below" — it reads as broken, which is the one thing a page
 * about what you are handing over cannot afford to look. CSS cannot round a
 * height to a multiple of something it has not measured, so this measures: the
 * first row's real height, then as many whole rows as fit the budget.
 *
 * Re-measured on resize, and on the rows changing — a caveat line under a
 * connection row makes it taller than the plain ones, so the first row is a
 * sample, not a promise. It holds for the uniform runs, which is where the
 * cutting looked worst.
 */
function TierList({ capped, children }) {
  const box = useRef(null);
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    if (!capped) {
      setHeight(null);
      box.current?.closest('.gp-ask')?.style.setProperty('--gp-sbw', '0px');
      return undefined;
    }
    const fit = () => {
      const el = box.current;
      const row = el?.querySelector('.gp-card');
      if (!el || !row) return;
      // THE BAR'S WIDTH IS MEASURED, NOT ASSUMED. A scrollbar takes its width out
      // of THIS list's content and nothing out of the capability list below it,
      // so the two columns of ✓/× drift apart by exactly that much — and how
      // much it is depends on the browser, the platform and the user's settings,
      // which is why two rounds of guessing 13px produced two different
      // mismatches. `offsetWidth - clientWidth` is the number itself; the other
      // list pays the same toll in padding (see --gp-sbw in Ask.css).
      const bar = el.offsetWidth - el.clientWidth;
      el.closest('.gp-ask')?.style.setProperty('--gp-sbw', `${bar}px`);
      const h = row.getBoundingClientRect().height;
      if (h <= 0) return;
      // WHAT SITS ABOVE THE FIRST ROW COUNTS TOO. The tier label scrolls inside
      // this box, so a height of exactly N rows is N rows MINUS the label — and
      // the list ended on a row sliced through its middle, which is the one thing
      // the whole-rows cap exists to prevent. Measured from the container's own
      // top so it holds for the note a connections tier adds as well.
      const lead = row.getBoundingClientRect().top - el.getBoundingClientRect().top;
      // TWELVE ROWS, OR WHAT THE WINDOW ALLOWS — whichever is fewer.
      //
      // Twelve because most asks are shorter than that, so the scrollbar stops
      // being a thing every reader meets and becomes something only a greedy app
      // produces. The window keeps a veto: on a short screen twelve rows would
      // push the realm chooser off the bottom, which is the one thing the cap
      // exists to prevent. Six is the floor — below that the list is a peephole,
      // and a peephole is worse than a long page.
      // The window's veto is a SAFETY, not the usual case: it only bites when
      // twelve rows would leave no room for the realm chooser under them. At 62%
      // it was biting on an ordinary laptop window and quietly serving nine.
      const fits = Math.floor(((window.innerHeight * 0.8) - lead) / h);
      setHeight(lead + Math.max(6, Math.min(12, fits)) * h);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [capped, children]);

  return (
    // tabIndex: a region that only scrolls is unreachable from a keyboard
    // otherwise — the same rule the plugin list follows.
    <div ref={box} className={`gp-ask__box${capped ? ' is-capped' : ''}`}
         style={height ? { maxHeight: `${height}px` } : undefined}
         tabIndex={capped ? 0 : undefined}
         role={capped ? 'group' : undefined}
         aria-label={capped ? 'Everything this app asks for, scrollable' : undefined}>
      {children}
    </div>
  );
}

/** G1 — the bucket header. Names its axis, so the page is never quietly clever. */
export function Bucket({ title, note, showLabel = true, continues = false, children }) {
  // No axis chip: there is one grouping now, so announcing it every time said
  // nothing. It existed to stop the page being quietly clever about choosing
  // between two — and there is no longer a choice to disclose.
  //
  // `note` states a RULE the grant is already subject to, not advice — the
  // connections tier uses it to say that a link is only visible when both its
  // ends are. It sits under the heading because it qualifies every card below
  // it, and reading it after the cards would be reading it too late.
  return (
    <section className="gp-rows">
      {/* SECTION vs TIER, told apart by CASE and register — they were both
          headings a pixel apart in size, so the page read as five siblings
          rather than two sections holding three tiers. A <p>, not a heading:
          the rows are the only divs in here, which is what keeps
          :first-of-type / :last-of-type addressing the rows and not the label. */}
      {/* NO COUNT. "YOUR GRAPH 3" numbered a list you can see the length of.
          Capping the list did not change that: "32 types · 32 out of reach" put
          two numbers in the quietest line on the page, in the register reserved
          for naming the axis — and the second one restated the verdict, in a
          second place, in different words. One verdict, on the union.
          And no label at all when there is only ONE tier — naming a single
          group says nothing except that grouping happened. */}
      {showLabel && !continues && <p className="gp-rows__lab">{title}</p>}
      {/* The note is NOT conditional on the label. It states a rule the grant is
          subject to either way, so an ask made only of relationship schemas —
          one bucket, no labels — must still be told that both ends have to be
          shared. It used to ride on `showLabel` and vanish in exactly that case. */}
      {note && <p className={`gp-rows__note${continues ? ' is-lede' : ''}`}>{note}</p>}
      {children}
    </section>
  );
}

/**
 * C5 — the ask, actionable.
 *
 * @param {Array}   buckets    groupGrants() output
 * @param {boolean} editable   false = C3 (the request), true = C5 (the grant)
 * @param {Function} isOn      (grant) => boolean
 * @param {Function} onToggle  (grant) => void
 * @param {Object}  scope      { [bucketId]: 'own' | 'any' }
 */
export default function Ask({
  buckets = [], editable = false, isOn = () => true, available, onToggle,
  onToggleMany, counts = {}, scope = {}, onScope, consequenceFor, items, askGrants = [],
  scopeWideLabel, pickedTypes, onToggleType, decided = true, appName,
  owner = 'your', blockedBy, isNew, faceOf,
}) {
  // Node permissions are ABOUT something — a schema — so they group under it.
  // Everything else is a capability with no object to group under, and reads
  // better as a sentence than as a card with one chip in it.
  const allNodeBuckets = buckets
    .map((b) => ({ ...b, cards: b.cards.filter(
      (c) => c.grants.some((g) => g.resource === 'nodes')) }))
    .filter((b) => b.cards.length);
  // THE BREADTH IS NOT ONE OF THE TYPES. A schemaless nodes card is lifted out
  // of the tiers entirely and led with — see AllData. Left in, it sorted among
  // the schemas as a type called "Everything".
  const isWide = (c) => c.schemaless && c.grants.some((g) => g.resource === 'nodes');
  const wideCards = allNodeBuckets.flatMap((b) => b.cards.filter(isWide));
  const nodeBuckets = allNodeBuckets
    .map((b) => ({ ...b, cards: b.cards.filter((c) => !isWide(c)) }))
    .filter((b) => b.cards.length);
  if (!allNodeBuckets.length && !items?.length) return null;
  const capped = nodeBuckets.reduce((n, b) => n + b.cards.length, 0) > 12;
  return (
    <div className="gp-ask" data-testid="ask" data-editable={String(editable)}>
      {/* The explanation lives on the Section heading now — printing it here as
          well said the same sentence twice, one line apart. */}
      {/* ONE CAP, AROUND THE WHOLE LIST — never one per tier.
          Capping each tier separately put an edge between the types and the
          connections that describes nothing: the tiers are one list read top to
          bottom, so a boundary drawn inside it reads as a divider someone chose
          rather than the end of what fits. The only honest place for an edge is
          the end of the list.
          The threshold is a HEIGHT, not a taste: a dozen rows is about where the
          realm chooser — the only control on this page — leaves the bottom of a
          laptop window. Shorter than that, the list costs nothing and a box
          around it is decoration.
          A CAP CHOOSES NOTHING, which is what the fold it replaced could not
          say: every row keeps its place and its order, and only how much you see
          at once changes. */}
      {/* THE FIRST TIER'S NAME SITS ABOVE THE SCROLL, not inside it. A heading
          that scrolls is a heading that leaves, and the scrollbar beside it
          measured a region whose first item was a label rather than a row — so
          the bar began level with the word `identity` and the list it described
          started 30px lower. Out here it names the list and stays; the bar now
          starts exactly at the first row and ends at the last.
          Later tiers keep their labels inside: they mark a change WITHIN the
          list, which is a different job from naming it. */}
      {/* ABOVE THE CAP AND ABOVE THE TIERS: it is the statement the rows below
          refine, and a row inside a scrolling region can leave the screen. */}
      <AllData cards={wideCards} isOn={isOn} available={available} decided={decided}
               owner={owner} />
      {wideCards.length > 0 && nodeBuckets.length > 0 && (
        <p className="gp-rows__lab">It also asks for</p>
      )}
      {capped && nodeBuckets.length > 1 && (
        <p className="gp-rows__lab gp-ask__lead">{nodeBuckets[0].title}</p>
      )}
      <TierList capped={capped}>
      {nodeBuckets.map((b, i) => (
        <Bucket key={b.id} title={b.title} note={b.note} continues={b.continues}
                showLabel={nodeBuckets.length > 1 && !(capped && i === 0)}>
          {b.cards.map((card) => (
            <SchemaCard
              key={card.key}
              card={card}
              count={counts[card.key]}
              editable={editable}
              isOn={isOn}
              available={available}
              onToggle={onToggle}
              pickedTypes={pickedTypes}
              onToggleType={onToggleType}
              decided={decided}
              blockedBy={blockedBy}
              isNew={isNew}
              faceOf={faceOf}
            />
          ))}
        </Bucket>
      ))}
      {/* THE SCOPE QUESTION CLOSES THE WHOLE NODES SECTION.

          It used to render inside the graph tier, on the reasoning that only
          that half has "entries it didn't create". But look at what it is
          actually asked over: `verbs` is the union across EVERY node bucket,
          and the answer is stored once, under `all`. It governed the
          connections rows the entire time — it simply sat above them, and once
          the connections tier became a continuation rather than its own
          section, the control was stranded mid-list qualifying rows that came
          after it.

          One question, asked once, positioned where its scope actually ends.

          `scope_type` is stored per VERB across every schema that verb touches,
          so a control per tier would ask the same question twice with
          overlapping verb lists and store the answers separately.

          THE VERBS ASKED FOR, not the ones currently reached. Filtering on
          `isOn` made the control appear the moment a realm was ticked — the
          last thing still moving the page under the cursor. `scope_type` is
          stored on the consent whatever the realm reaches, so the question is
          askable from the start. */}
      </TierList>
      {/* OUTSIDE THE CAP, DELIBERATELY. It is the only control in the ask, and
          inside a region capped to a whole number of rows it landed exactly past
          the last one — reliably below the fold, on every long list. Only the
          ROWS scroll; the question they all answer to sits under them where it
          can always be seen and reached. */}
      {editable && (
        <div className="gp-ask__scope">
          <Scope
            verbs={[...new Set(nodeBuckets.flatMap((nb) => nb.scopedVerbs.filter((v) =>
              nb.cards.some((c) => c.grants.some((g) => g.verb === v)))))].sort()}
            value={scope.all || 'any'}
            onChange={(v) => onScope?.('all', v)}
            consequence={consequenceFor?.('all', scope.all || 'any')}
            wideLabel={scopeWideLabel}
            appName={appName}
          />
        </div>
      )}
      {/* ITS OWN BOX, OUTSIDE THE CAP. A capability has no schema axis at all —
          grantAxis calls that "the real seam" — so it is not a tier of the type
          list and does not belong under a heading that says `identity graph
          nodes`. Nor does it belong inside the SCROLL: three rows that fit are
          reachable at a glance, and putting them past the bottom of a capped
          list made the shortest part of the ask the hardest part to find. Its
          own frame keeps it aligned with everything else and out of the way of
          the thing that actually needed capping. */}
      {(categorizeDlr(items || [], [], null, {}).capabilities || []).length > 0 && (
        <div className="gp-ask__box">
        <section className="gp-rows">
          {/* TWO CLUSTERS, ONE GAP. A schema permission is scoped to a type in
              your graph; a capability has no schema axis at all — grantAxis
              calls that "the real seam", and it is a fact about the resource,
              not about how dangerous the verb is. */}
          {/* "Other access" — these are permissions like the rows above, they
              simply have no schema to hang on. "Actions" read as a list of
              things it DOES, which is a different claim. */}
          <p className="gp-rows__lab">Other access</p>
          <Capabilities items={items} grants={askGrants} isOn={isOn} decided={decided} />
        </section>
        </div>
      )}
    </div>
  );
}
